/**
 * Gem Create Modal — shows available gem types as clickable tiles.
 * Opened on click of the "Create" action button (mobile and desktop).
 * Keyboard hotkeys 1-8 continue to work without opening this modal.
 */

import { t } from "../../i18n/index.js";

const ALL_GEM_TYPES = [
  "Yellow",
  "Orange",
  "Red",
  "Purple",
  "Green",
  "Blue",
  "Cyan",
  "Lime",
] as const;

const GEM_COLOR: Record<string, string> = {
  Yellow: "#ffe55a",
  Orange: "#ff8c20",
  Red: "#d63030",
  Purple: "#a45ae5",
  Green: "#30c830",
  Blue: "#4a8dc4",
  Cyan: "#5acff0",
  Lime: "#a8e030",
};

const LIGHT_GEM = new Set(["Yellow", "Orange", "Green", "Cyan", "Lime"]);

export interface GemCreateModalOptions {
  anchor: HTMLElement;
  /** Subset of gem types to display. Undefined = show all 8. */
  allowedTypes?: string[];
  onSelect: (gemType: string) => void;
}

let activeOverlay: HTMLElement | null = null;

type ModalOverlay = HTMLElement & { __cleanup?: () => void };

export function openGemCreateModal(opts: GemCreateModalOptions): void {
  // Close any existing modal first.
  closeGemCreateModal();

  const types = opts.allowedTypes?.length
    ? ALL_GEM_TYPES.filter((t) => opts.allowedTypes!.includes(t))
    : [...ALL_GEM_TYPES];

  const overlay = document.createElement("div");
  overlay.className = "gem-create-modal";
  overlay.style.cssText = `
    position: fixed;
    z-index: 9999;
    background: rgba(20,12,4,0.92);
    border: 1px solid rgba(255,200,100,0.4);
    border-radius: 8px;
    padding: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 240px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.8);
  `;

  // Hotkey labels (1-based, matching keyboard keys 1-8)
  const hotkeyMap: Record<string, string> = {
    Yellow: "1",
    Orange: "2",
    Red: "3",
    Purple: "4",
    Green: "5",
    Blue: "6",
    Cyan: "7",
    Lime: "8",
  };

  for (const gemType of types) {
    const btn = document.createElement("button");
    btn.type = "button";
    const color = GEM_COLOR[gemType] ?? "#888";
    const isLight = LIGHT_GEM.has(gemType);
    btn.style.cssText = `
      background: ${color};
      color: ${isLight ? "#000" : "#fff"};
      border: 2px solid rgba(255,255,255,0.3);
      border-radius: 6px;
      width: 52px;
      height: 52px;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      font-size: 10px;
      font-weight: bold;
      line-height: 1.1;
      padding: 0;
    `;

    const nameSpan = document.createElement("span");
    nameSpan.textContent = t(`gem.name.${gemType}`);
    nameSpan.style.cssText = "font-size:9px; text-align:center; line-height:1.1;";

    const hotkeySpan = document.createElement("span");
    hotkeySpan.textContent = `[${hotkeyMap[gemType] ?? "?"}]`;
    hotkeySpan.style.cssText = "font-size:8px; opacity:0.7;";

    btn.appendChild(nameSpan);
    btn.appendChild(hotkeySpan);

    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      opts.onSelect(gemType);
      closeGemCreateModal();
    });

    btn.addEventListener("mouseenter", () => {
      btn.style.borderColor = "rgba(255,255,255,0.8)";
      btn.style.transform = "scale(1.05)";
    });
    btn.addEventListener("mouseleave", () => {
      btn.style.borderColor = "rgba(255,255,255,0.3)";
      btn.style.transform = "";
    });

    overlay.appendChild(btn);
  }

  // Position the modal above/below the anchor button.
  document.body.appendChild(overlay);
  activeOverlay = overlay;

  // Position after DOM insertion so offsetHeight is available.
  positionModal(overlay, opts.anchor);

  // Close on click outside or ESC.
  const onOutside = (e: PointerEvent) => {
    if (!overlay.contains(e.target as Node) && e.target !== opts.anchor) {
      closeGemCreateModal();
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") closeGemCreateModal();
  };

  // Add listeners with a tiny delay so the click that opened the modal doesn't immediately close it.
  setTimeout(() => {
    document.addEventListener("pointerdown", onOutside, { capture: true });
    document.addEventListener("keydown", onKey, { capture: true });
    (overlay as ModalOverlay).__cleanup = () => {
      document.removeEventListener("pointerdown", onOutside, { capture: true });
      document.removeEventListener("keydown", onKey, { capture: true });
    };
  }, 0);
}

function positionModal(modal: HTMLElement, anchor: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  const mw = modal.offsetWidth || 240;
  const mh = modal.offsetHeight || 120;
  const margin = 6;

  // Prefer to the left of the anchor (right-column sits on right edge)
  let x = r.left - mw - margin;
  if (x < margin) x = r.right + margin;
  if (x + mw > window.innerWidth - margin) x = window.innerWidth - mw - margin;

  // Prefer aligned to anchor top, shift up if needed
  let y = r.top;
  if (y + mh > window.innerHeight - margin) y = window.innerHeight - mh - margin;
  if (y < margin) y = margin;

  modal.style.left = `${x}px`;
  modal.style.top = `${y}px`;
}

export function closeGemCreateModal(): void {
  if (activeOverlay) {
    const cleanup = (activeOverlay as ModalOverlay).__cleanup;
    if (cleanup) cleanup();
    activeOverlay.remove();
    activeOverlay = null;
  }
}
